import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Search,
  FileText,
  Download,
  Loader2,
  BarChart3,
  X,
  Plus,
  Package,
  Check,
  ChevronDown,
  ChevronRight,
  ShoppingBag,
  Target,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { DateInputBR } from '@components/DateInputBR';
import { salesReportService, SalesReportRow, SalesReportSummary, SalesReportFilters, SalesReportProductRow, SalesReportProductsSummary, SalesReportProductsFilters } from '../../services/salesReportService';
import { salesOrderService } from '../../services/salesOrderService';
import { SalesOrderItem, Seller, OrderPaymentMethod } from '../../types/salesOrder';
import { productService } from '../../services/productService';
import { Product } from '../../types/product';
import { SALES_ORDER_STATUS_OPTIONS, OrderStatusId } from '../../types/salesOrder';
import { orderTypeService } from '../../services/orderTypeService';
import { OrderType } from '../../types/orderType';
import { salesChannelService } from '../../services/salesChannelService';
import { SalesChannel } from '../../types/salesChannel';
import { userPreferencesService } from '../../services/userPreferencesService';
import { SalesReportGoalTab } from './SalesReportGoalTab';
import { CompanyFilterPills } from './CompanyFilterPills';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (value?: number) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : 'R$ 0,00';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

const getToday = () => new Date().toISOString().split('T')[0];

/** Returns first day of current month (YYYY-MM-DD) */
const getFirstOfCurrentMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

/** Returns first day (Sunday) of the current week (YYYY-MM-DD) */
const getFirstOfCurrentWeek = () => {
  const d = new Date();
  // getDay(): 0 = domingo … 6 = sábado. Semana começa no domingo.
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay()).toISOString().split('T')[0];
};

// ─── Date presets ────────────────────────────────────────────────

type DatePreset = 'today' | 'week' | 'month' | 'custom';

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'week', label: 'Semana atual' },
  { key: 'month', label: 'Mês atual' },
  { key: 'custom', label: 'Personalizado' },
];

/** Resolves a preset to [dateFrom, dateTo] (YYYY-MM-DD); custom returns null (keep current). */
const resolveDatePreset = (preset: DatePreset): [string, string] | null => {
  if (preset === 'custom') return null;
  const to = getToday();
  switch (preset) {
    case 'today': return [to, to];
    case 'week':  return [getFirstOfCurrentWeek(), to];
    case 'month': return [getFirstOfCurrentMonth(), to];
  }
};

const getPageRange = (current: number, total: number): (number | '...')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

// Status badge
const STATUS_BADGE_CLASSES: Record<number, string> = {
  [OrderStatusId.EmAberto]:       'bg-yellow-100 text-yellow-800',
  [OrderStatusId.EmDigitacao]:    'bg-blue-100 text-blue-700',
  [OrderStatusId.EmAndamento]:    'bg-blue-100 text-blue-700',
  [OrderStatusId.Atendido]:       'bg-green-100 text-green-700',
  [OrderStatusId.EmConferencia]:  'bg-blue-200 text-blue-800',
  [OrderStatusId.Cancelado]:      'bg-red-100 text-red-700',
  [OrderStatusId.Conferido]:      'bg-blue-300 text-blue-900',
  [OrderStatusId.VendaAgenciada]: 'bg-amber-100 text-amber-700',
  [OrderStatusId.Entregue]:       'bg-teal-100 text-teal-700',
  [OrderStatusId.DesmembradoPai]: 'bg-gray-100 text-gray-700',   // cinza claro
};

const getStatusBadge = (status?: string, statusId?: number) => {
  if (!status && !statusId) return <span className="text-slate-500">—</span>;
  const opt = statusId
    ? SALES_ORDER_STATUS_OPTIONS.find(o => o.id === statusId)
    : SALES_ORDER_STATUS_OPTIONS.find(o => o.value.toLowerCase() === (status ?? '').toLowerCase());
  const label = opt?.label ?? status ?? '—';
  const cls = opt ? (STATUS_BADGE_CLASSES[opt.id] ?? 'bg-slate-100 text-slate-700') : 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
};

// ─── Product Search Modal ────────────────────────────────────────

interface ProductSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (product: Product) => void;
  excludeSkus: string[];
}

const ProductSearchModal: React.FC<ProductSearchModalProps> = ({ isOpen, onClose, onSelect, excludeSkus }) => {
  const [query, setQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setProducts([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setProducts([]); return; }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const filters: { is_active?: boolean } = {};
        if (activeOnly) filters.is_active = true;
        const response = await productService.searchProducts(query, 1, 20, filters);
        setProducts(response.products.filter(p => !excludeSkus.includes(p.sku)));
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, activeOnly, excludeSkus]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Pesquisar Produto</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Search input */}
        <div className="p-4 space-y-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              ref={inputRef}
              type="text"
              className="input pl-10 w-full"
              placeholder="Pesquisar por nome ou SKU..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={e => setActiveOnly(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            Somente produtos ativos
          </label>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-emerald-600" size={24} />
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Package size={36} className="mb-2" />
              <p className="text-sm">{query ? 'Nenhum produto encontrado' : 'Digite para pesquisar'}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {products.map(product => (
                <button
                  key={product.id}
                  onClick={() => { onSelect(product); setQuery(''); setProducts([]); }}
                  className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-emerald-50 transition-colors group"
                >
                  <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center text-slate-400 group-hover:bg-emerald-100 group-hover:text-emerald-600 transition-colors">
                    <Package size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{product.description}</p>
                    <p className="text-xs text-slate-500">SKU: {product.sku} {product.price ? `| ${formatCurrency(product.price)}` : ''}</p>
                  </div>
                  {!product.is_active && (
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Inativo</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Excel Export (client-side) ──────────────────────────────────

// Formata número no padrão pt-BR para CSV (separador decimal = vírgula)
const fmtNum = (v: number | undefined | null, decimals = 2): string =>
  (v ?? 0).toFixed(decimals).replace('.', ',');

// ─── Products Excel Export ───────────────────────────────────────

function exportProductsToExcel(rows: SalesReportProductRow[], summary: SalesReportProductsSummary) {
  const headers = ['SKU', 'Descrição do Produto', 'Quantidade Vendida', 'Valor da Venda', 'Desconto', 'Valor Total'];

  const csvRows = rows.map(r => [
    r.sku,
    `"${(r.description || '').replace(/"/g, '""')}"`,
    r.quantity_sold,
    fmtNum(r.sale_value),
    fmtNum(r.discount_value),
    fmtNum(r.total_value),
  ]);

  // Summary row
  csvRows.push([]);
  csvRows.push(['', 'TOTAIS', summary.total_quantity, fmtNum(summary.total_sale_value), fmtNum(summary.total_discount_value), fmtNum(summary.total_value)]);

  const BOM = '\uFEFF';
  const content = BOM + [headers.join(';'), ...csvRows.map(r => r.join(';'))].join('\r\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `relatorio-produtos-vendidos-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ─── Products Report Tab ─────────────────────────────────────────

const SalesReportProductsTab: React.FC = () => {
  // Filters
  const [dateFrom, setDateFrom] = useState(getFirstOfCurrentMonth);
  const [dateTo, setDateTo] = useState(getToday);
  const [datePreset, setDatePreset] = useState<DatePreset>('month'); // default = mês atual
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
  const [selectedOrderTypeIds, setSelectedOrderTypeIds] = useState<number[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [selectedIsPayed, setSelectedIsPayed] = useState<boolean | undefined>(undefined);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Data
  const [rows, setRows] = useState<SalesReportProductRow[]>([]);
  const [summary, setSummary] = useState<SalesReportProductsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50;

  // PDF
  const [pdfLoading, setPdfLoading] = useState(false);

  // Load lookups
  useEffect(() => {
    orderTypeService.listAll().then(setOrderTypes).catch(() => {});
    // Load user preferences
    userPreferencesService.getPreferences('SALES_REPORT_PRODUCTS').then(prefs => {
      if (prefs) {
        if (prefs.order_status_ids) setSelectedStatuses(prefs.order_status_ids);
        if (prefs.order_type_ids) setSelectedOrderTypeIds(prefs.order_type_ids);
        if (prefs.company_ids) setSelectedCompanyIds(prefs.company_ids);
        if (prefs.is_payed !== undefined) setSelectedIsPayed(prefs.is_payed);
      }
      setPreferencesLoaded(true);
    }).catch(() => setPreferencesLoaded(true));
  }, []);

  // Load report
  const loadReport = useCallback(async () => {
    if (!preferencesLoaded) return;
    try {
      setLoading(true);
      setError(null);

      const filters: SalesReportProductsFilters = {
        date_from: dateFrom,
        date_to: dateTo,
        order_status_ids: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        order_type_ids: selectedOrderTypeIds.length > 0 ? selectedOrderTypeIds : undefined,
        company_ids: selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined,
        is_payed: selectedIsPayed,
        page: currentPage,
        pageSize,
      };

      const response = await salesReportService.getProductsReport(filters);
      setRows(response.data);
      setSummary(response.summary);
      setTotalItems(response.pagination.total);
      setTotalPages(response.pagination.totalPages);

      // Save preferences
      userPreferencesService.savePreferences('SALES_REPORT_PRODUCTS', {
        order_status_ids: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        order_type_ids: selectedOrderTypeIds.length > 0 ? selectedOrderTypeIds : undefined,
        company_ids: selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined,
        is_payed: selectedIsPayed,
      }).catch(() => {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedStatuses, selectedOrderTypeIds, selectedCompanyIds, selectedIsPayed, currentPage, preferencesLoaded]);

  useEffect(() => { loadReport(); }, [loadReport]);

  // Date presets
  const applyDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    setCurrentPage(1);
    const range = resolveDatePreset(preset);
    if (range) {
      setDateFrom(range[0]);
      setDateTo(range[1]);
    }
  };

  // Status Toggle
  const toggleStatus = (statusId: number) => {
    setSelectedStatuses(prev =>
      prev.includes(statusId) ? prev.filter(s => s !== statusId) : [...prev, statusId]
    );
    setCurrentPage(1);
  };

  const toggleAllStatuses = () => {
    if (selectedStatuses.length === SALES_ORDER_STATUS_OPTIONS.length) {
      setSelectedStatuses([]);
    } else {
      setSelectedStatuses(SALES_ORDER_STATUS_OPTIONS.map(o => o.id));
    }
    setCurrentPage(1);
  };

  // Export
  const handleExportExcel = () => {
    if (rows.length === 0) return;
    exportProductsToExcel(rows, summary ?? { total_products: 0, total_quantity: 0, total_sale_value: 0, total_discount_value: 0, total_value: 0 });
  };

  const handleExportPdf = async () => {
    try {
      setPdfLoading(true);
      await salesReportService.downloadProductsPdf({
        date_from: dateFrom,
        date_to: dateTo,
        order_status_ids: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        order_type_ids: selectedOrderTypeIds.length > 0 ? selectedOrderTypeIds : undefined,
        company_ids: selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined,
        is_payed: selectedIsPayed,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">
            Listagem consolidada de produtos vendidos no período
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            icon={<Download size={16} />}
            onClick={handleExportExcel}
            disabled={rows.length === 0 || loading}
          >
            Exportar Excel
          </Button>
          <Button
            variant="outline"
            icon={<FileText size={16} />}
            onClick={handleExportPdf}
            loading={pdfLoading}
            disabled={rows.length === 0 || loading}
          >
            Imprimir PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader title="Filtros" />
        <CardContent className="space-y-4">
          {/* Date Range */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Período</label>
            {/* Presets de período */}
            <div className="flex flex-wrap gap-2 mb-3">
              {DATE_PRESETS.map(preset => {
                const isSelected = datePreset === preset.key;
                return (
                  <button
                    key={preset.key}
                    onClick={() => applyDatePreset(preset.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                      isSelected
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {isSelected && <Check size={12} />}
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Data inicial</label>
                <DateInputBR
                  className="input text-sm"
                  value={dateFrom}
                  onChange={v => { setDateFrom(v); setDatePreset('custom'); setCurrentPage(1); }}
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Data final</label>
                <DateInputBR
                  className="input text-sm"
                  value={dateTo}
                  onChange={v => { setDateTo(v); setDatePreset('custom'); setCurrentPage(1); }}
                />
              </div>
            </div>
          </div>

          {/* Status checkboxes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Status do Pedido</label>
              <button
                onClick={toggleAllStatuses}
                className="text-xs text-emerald-600 hover:underline"
              >
                {selectedStatuses.length === SALES_ORDER_STATUS_OPTIONS.length ? 'Desmarcar todos' : 'Marcar todos'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {SALES_ORDER_STATUS_OPTIONS.map(opt => {
                const isSelected = selectedStatuses.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    onClick={() => toggleStatus(opt.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                      isSelected
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {isSelected && <Check size={12} />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Company filter */}
          <CompanyFilterPills selected={selectedCompanyIds} onChange={ids => { setSelectedCompanyIds(ids); setCurrentPage(1); }} />

          {/* Order Type filter */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Tipo de Pedido</label>
              <button
                onClick={() => {
                  if (selectedOrderTypeIds.length === orderTypes.length) {
                    setSelectedOrderTypeIds([]);
                  } else {
                    setSelectedOrderTypeIds(orderTypes.map(t => t.order_type_id));
                  }
                  setCurrentPage(1);
                }}
                className="text-xs text-emerald-600 hover:underline"
              >
                {selectedOrderTypeIds.length === orderTypes.length ? 'Desmarcar todos' : 'Marcar todos'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {orderTypes.map(ot => {
                const isSelected = selectedOrderTypeIds.includes(ot.order_type_id);
                return (
                  <button
                    key={ot.order_type_id}
                    onClick={() => {
                      setSelectedOrderTypeIds(prev =>
                        prev.includes(ot.order_type_id)
                          ? prev.filter(id => id !== ot.order_type_id)
                          : [...prev, ot.order_type_id]
                      );
                      setCurrentPage(1);
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                      isSelected
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {isSelected && <Check size={12} />}
                    {ot.order_type}
                  </button>
                );
              })}
            </div>
          </div>

          {/* IsPayed filter */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Status de Pagamento</label>
            <select
              className="input text-sm w-full max-w-xs"
              value={selectedIsPayed === undefined ? '' : selectedIsPayed ? 'true' : 'false'}
              onChange={e => {
                const val = e.target.value;
                setSelectedIsPayed(val === '' ? undefined : val === 'true');
                setCurrentPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="true">Pago</option>
              <option value="false">Não Pago</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && !loading && rows.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white rounded-lg border border-slate-200 p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Produtos Distintos</p>
            <p className="text-lg font-bold text-slate-900 mt-1">{summary.total_products}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Qtd Total Vendida</p>
            <p className="text-lg font-bold text-slate-900 mt-1">{summary.total_quantity}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Valor da Venda</p>
            <p className="text-lg font-bold text-slate-900 mt-1">{formatCurrency(summary.total_sale_value)}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Total Desconto</p>
            <p className="text-lg font-bold text-red-600 mt-1">{formatCurrency(summary.total_discount_value)}</p>
          </div>
          <div className="bg-white rounded-lg border border-emerald-200 p-3 bg-emerald-50">
            <p className="text-xs text-emerald-600 uppercase tracking-wider font-medium">Valor Total</p>
            <p className="text-lg font-bold text-emerald-700 mt-1">{formatCurrency(summary.total_value)}</p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Fechar</button>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-emerald-600" size={32} />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Package size={48} className="mb-3" />
              <p className="text-lg font-medium">Nenhum resultado encontrado</p>
              <p className="text-sm mt-1">Ajuste os filtros ou período de busca.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <TableHeader>
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">SKU</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição do Produto</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Qtd Vendida</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor da Venda</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Desconto</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor Total</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {rows.map(row => (
                    <TableRow key={row.sku}>
                      <TableCell className="font-mono font-semibold text-slate-800">{row.sku}</TableCell>
                      <TableCell className="text-slate-700">{row.description || '—'}</TableCell>
                      <TableCell className="text-right font-medium text-slate-800">{row.quantity_sold}</TableCell>
                      <TableCell className="text-right font-medium text-slate-800">{formatCurrency(row.sale_value)}</TableCell>
                      <TableCell className="text-right text-red-600">{row.discount_value ? formatCurrency(row.discount_value) : '—'}</TableCell>
                      <TableCell className="text-right font-bold text-emerald-700">{formatCurrency(row.total_value)}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
                {summary && (
                  <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                    <tr>
                      <td colSpan={2} className="py-3 px-4 text-right font-bold text-slate-700 uppercase text-sm">
                        Totais ({summary.total_products} produtos)
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-slate-900">{summary.total_quantity}</td>
                      <td className="py-3 px-4 text-right font-bold text-slate-900">{formatCurrency(summary.total_sale_value)}</td>
                      <td className="py-3 px-4 text-right font-bold text-red-600">{formatCurrency(summary.total_discount_value)}</td>
                      <td className="py-3 px-4 text-right font-bold text-emerald-700 text-lg">{formatCurrency(summary.total_value)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-1">
        <p className="text-sm text-slate-500 order-2 sm:order-1">
          {totalItems > 0
            ? `${((currentPage - 1) * pageSize) + 1}–${Math.min(currentPage * pageSize, totalItems)} de ${totalItems} registro${totalItems !== 1 ? 's' : ''} · Página ${currentPage} de ${totalPages}`
            : 'Nenhum registro'}
        </p>
        {totalPages > 1 && (
          <div className="flex items-center gap-1 order-1 sm:order-2">
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(1)}
              title="Primeira página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >«</button>
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
              title="Página anterior"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >‹</button>
            {getPageRange(currentPage, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ell-${i}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-sm">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(Number(p))}
                  title={`Página ${p}`}
                  className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${
                    currentPage === p
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >{p}</button>
              )
            )}
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              title="Próxima página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >›</button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              title="Última página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >»</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────

export const SalesReportView: React.FC = () => {
  // Active tab
  const [activeTab, setActiveTab] = useState<'orders' | 'products' | 'goal'>('orders');

  // Filters
  const [dateFrom, setDateFrom] = useState(getFirstOfCurrentMonth);
  const [dateTo, setDateTo] = useState(getToday);
  const [datePreset, setDatePreset] = useState<DatePreset>('month'); // default = mês atual
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<{ sku: string; description: string }[]>([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedSellerId, setSelectedSellerId] = useState<number | undefined>(undefined);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [selectedOrderTypeIds, setSelectedOrderTypeIds] = useState<number[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [selectedSalesChannelIds, setSelectedSalesChannelIds] = useState<number[]>([]);
  const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([]);
  const [selectedPaymentMethodIds, setSelectedPaymentMethodIds] = useState<number[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<OrderPaymentMethod[]>([]);
  const [selectedIsPayed, setSelectedIsPayed] = useState<boolean | undefined>(undefined);
  const [selectedIsFirstPurchase, setSelectedIsFirstPurchase] = useState<boolean | undefined>(undefined);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Data
  const [rows, setRows] = useState<SalesReportRow[]>([]);
  const [summary, setSummary] = useState<SalesReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50;

  // PDF
  const [pdfLoading, setPdfLoading] = useState(false);

  // Expand rows
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [orderItemsCache, setOrderItemsCache] = useState<Record<number, { loading: boolean; items: SalesOrderItem[] }>>({}); 

  const toggleExpand = async (orderId: number) => {
    if (expandedOrderId === orderId) { setExpandedOrderId(null); return; }
    setExpandedOrderId(orderId);
    if (!orderItemsCache[orderId]) {
      setOrderItemsCache(prev => ({ ...prev, [orderId]: { loading: true, items: [] } }));
      try {
        const fullOrder = await salesOrderService.getOrderById(orderId);
        setOrderItemsCache(prev => ({ ...prev, [orderId]: { loading: false, items: fullOrder.items || [] } }));
      } catch {
        setOrderItemsCache(prev => ({ ...prev, [orderId]: { loading: false, items: [] } }));
      }
    }
  };

  // ── Load Sellers ──────────────────────────────────────────────

  useEffect(() => {
    salesOrderService.getSellers().then(setSellers).catch(() => {});
    orderTypeService.listAll().then(setOrderTypes).catch(() => {});
    salesChannelService.listAll().then(setSalesChannels).catch(() => {});
    salesOrderService.getPaymentMethods().then(setPaymentMethods).catch(() => {});
    // Load user preferences
    userPreferencesService.getPreferences('SALES_REPORT').then(prefs => {
      if (prefs) {
        if (prefs.order_status_ids) setSelectedStatuses(prefs.order_status_ids);
        if (prefs.product_skus) setSelectedProducts(prefs.product_skus);
        if (prefs.seller_id !== undefined) setSelectedSellerId(prefs.seller_id);
        if (prefs.order_type_ids) setSelectedOrderTypeIds(prefs.order_type_ids);
        if (prefs.company_ids) setSelectedCompanyIds(prefs.company_ids);
        if (prefs.sales_channel_ids) setSelectedSalesChannelIds(prefs.sales_channel_ids);
        if (prefs.payment_method_ids) setSelectedPaymentMethodIds(prefs.payment_method_ids);
        if (prefs.is_payed !== undefined) setSelectedIsPayed(prefs.is_payed);
        if (prefs.is_first_purchase !== undefined) setSelectedIsFirstPurchase(prefs.is_first_purchase);
      }
      setPreferencesLoaded(true);
    }).catch(() => setPreferencesLoaded(true));
  }, []);

  // ── Load Report ──────────────────────────────────────────────

  const loadReport = useCallback(async () => {
    if (!preferencesLoaded) return;
    try {
      setLoading(true);
      setError(null);

      const filters: SalesReportFilters = {
        date_from: dateFrom,
        date_to: dateTo,
        order_status_ids: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        product_skus: selectedProducts.length > 0 ? selectedProducts.map(p => p.sku) : undefined,
        seller_id: selectedSellerId,
        order_type_ids: selectedOrderTypeIds.length > 0 ? selectedOrderTypeIds : undefined,
        company_ids: selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined,
        sales_channel_ids: selectedSalesChannelIds.length > 0 ? selectedSalesChannelIds : undefined,
        payment_method_ids: selectedPaymentMethodIds.length > 0 ? selectedPaymentMethodIds : undefined,
        is_payed: selectedIsPayed,
        is_first_purchase: selectedIsFirstPurchase,
        page: currentPage,
        pageSize,
      };

      const response = await salesReportService.getReport(filters);
      setRows(response.data);
      setSummary(response.summary);
      setTotalItems(response.pagination.total);
      setTotalPages(response.pagination.totalPages);

      // Save preferences (exclude dates — those use default current month)
      userPreferencesService.savePreferences('SALES_REPORT', {
        order_status_ids: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        product_skus: selectedProducts.length > 0 ? selectedProducts : undefined,
        seller_id: selectedSellerId,
        order_type_ids: selectedOrderTypeIds.length > 0 ? selectedOrderTypeIds : undefined,
        company_ids: selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined,
        sales_channel_ids: selectedSalesChannelIds.length > 0 ? selectedSalesChannelIds : undefined,
        payment_method_ids: selectedPaymentMethodIds.length > 0 ? selectedPaymentMethodIds : undefined,
        is_payed: selectedIsPayed,
        is_first_purchase: selectedIsFirstPurchase,
      }).catch(() => {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedStatuses, selectedProducts, selectedSellerId, selectedOrderTypeIds, selectedCompanyIds, selectedSalesChannelIds, selectedPaymentMethodIds, selectedIsPayed, selectedIsFirstPurchase, currentPage, preferencesLoaded]);

  useEffect(() => { loadReport(); }, [loadReport]);

  // ── Status Toggle ──────────────────────────────────────────

  const toggleStatus = (statusId: number) => {
    setSelectedStatuses(prev =>
      prev.includes(statusId) ? prev.filter(s => s !== statusId) : [...prev, statusId]
    );
    setCurrentPage(1);
  };

  const toggleAllStatuses = () => {
    if (selectedStatuses.length === SALES_ORDER_STATUS_OPTIONS.length) {
      setSelectedStatuses([]);
    } else {
      setSelectedStatuses(SALES_ORDER_STATUS_OPTIONS.map(o => o.id));
    }
    setCurrentPage(1);
  };

  // ── Date presets ──────────────────────────────────────────

  const applyDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    setCurrentPage(1);
    const range = resolveDatePreset(preset);
    if (range) {
      setDateFrom(range[0]);
      setDateTo(range[1]);
    }
  };

  // ── Product Management ────────────────────────────────────

  const addProduct = (product: Product) => {
    if (!selectedProducts.find(p => p.sku === product.sku)) {
      setSelectedProducts(prev => [...prev, { sku: product.sku, description: product.description }]);
      setCurrentPage(1);
    }
    setShowProductModal(false);
  };

  const removeProduct = (sku: string) => {
    setSelectedProducts(prev => prev.filter(p => p.sku !== sku));
    setCurrentPage(1);
  };

  // ── Export ────────────────────────────────────────────────

  const [excelLoading, setExcelLoading] = useState(false);

  const handleExportExcel = async () => {
    try {
      setExcelLoading(true);
      await salesReportService.downloadExcel({
        date_from: dateFrom,
        date_to: dateTo,
        order_status_ids: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        product_skus: selectedProducts.length > 0 ? selectedProducts.map(p => p.sku) : undefined,
        seller_id: selectedSellerId,
        order_type_ids: selectedOrderTypeIds.length > 0 ? selectedOrderTypeIds : undefined,
        company_ids: selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined,
        sales_channel_ids: selectedSalesChannelIds.length > 0 ? selectedSalesChannelIds : undefined,
        payment_method_ids: selectedPaymentMethodIds.length > 0 ? selectedPaymentMethodIds : undefined,
        is_payed: selectedIsPayed,
        is_first_purchase: selectedIsFirstPurchase,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar Excel');
    } finally {
      setExcelLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setPdfLoading(true);
      await salesReportService.downloadPdf({
        date_from: dateFrom,
        date_to: dateTo,
        order_status_ids: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        product_skus: selectedProducts.length > 0 ? selectedProducts.map(p => p.sku) : undefined,
        seller_id: selectedSellerId,
        order_type_ids: selectedOrderTypeIds.length > 0 ? selectedOrderTypeIds : undefined,
        company_ids: selectedCompanyIds.length > 0 ? selectedCompanyIds : undefined,
        sales_channel_ids: selectedSalesChannelIds.length > 0 ? selectedSalesChannelIds : undefined,
        payment_method_ids: selectedPaymentMethodIds.length > 0 ? selectedPaymentMethodIds : undefined,
        is_payed: selectedIsPayed,
        is_first_purchase: selectedIsFirstPurchase,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────

  return (
    <PermissionGuard permission="reports.sales" fallback={
      <div className="flex items-center justify-center py-20">
        <div className="text-center text-slate-400">
          <BarChart3 size={48} className="mx-auto mb-3" />
          <p className="text-lg font-medium">Acesso Negado</p>
          <p className="text-sm">Você não tem permissão para acessar o relatório de vendas.</p>
        </div>
      </div>
    }>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Relatório de Vendas</h1>
            <p className="text-sm text-slate-500 mt-1">
              Análise de pedidos de venda por período, status e produtos
            </p>
          </div>
          {activeTab === 'orders' && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                icon={<Download size={16} />}
                onClick={handleExportExcel}
                loading={excelLoading}
                disabled={loading}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outline"
                icon={<FileText size={16} />}
                onClick={handleExportPdf}
                loading={pdfLoading}
                disabled={loading}
              >
                Imprimir PDF
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <nav className="flex -mb-px gap-6">
            <button
              onClick={() => setActiveTab('orders')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'orders'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <ShoppingBag size={16} /> Pedidos
              </span>
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'products'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Package size={16} /> Produtos
              </span>
            </button>
            <button
              onClick={() => setActiveTab('goal')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'goal'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Target size={16} /> Meta de Venda
              </span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'goal' ? (
          <SalesReportGoalTab />
        ) : activeTab === 'products' ? (
          <SalesReportProductsTab />
        ) : (
        <>

        {/* Filters */}
        <Card>
          <CardHeader title="Filtros" />
          <CardContent className="space-y-4">
            {/* Date Range */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Período</label>
              {/* Presets de período */}
              <div className="flex flex-wrap gap-2 mb-3">
                {DATE_PRESETS.map(preset => {
                  const isSelected = datePreset === preset.key;
                  return (
                    <button
                      key={preset.key}
                      onClick={() => applyDatePreset(preset.key)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                        isSelected
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {isSelected && <Check size={12} />}
                      {preset.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Data inicial</label>
                  <DateInputBR
                    className="input text-sm"
                    value={dateFrom}
                    onChange={v => { setDateFrom(v); setDatePreset('custom'); setCurrentPage(1); }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Data final</label>
                  <DateInputBR
                    className="input text-sm"
                    value={dateTo}
                    onChange={v => { setDateTo(v); setDatePreset('custom'); setCurrentPage(1); }}
                  />
                </div>
              </div>
            </div>

            {/* Status checkboxes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Status do Pedido</label>
                <button
                  onClick={toggleAllStatuses}
                  className="text-xs text-emerald-600 hover:underline"
                >
                  {selectedStatuses.length === SALES_ORDER_STATUS_OPTIONS.length ? 'Desmarcar todos' : 'Marcar todos'}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {SALES_ORDER_STATUS_OPTIONS.map(opt => {
                  const isSelected = selectedStatuses.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => toggleStatus(opt.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                        isSelected
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {isSelected && <Check size={12} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {selectedStatuses.length > 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  {selectedStatuses.length} status selecionado{selectedStatuses.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Product filter */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Filtrar por Produtos</label>
                <Button
                  size="sm"
                  variant="outline"
                  icon={<Plus size={14} />}
                  onClick={() => setShowProductModal(true)}
                >
                  Adicionar Produto
                </Button>
              </div>
              {selectedProducts.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedProducts.map(p => (
                    <span
                      key={p.sku}
                      className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-3 py-1 text-xs font-medium"
                    >
                      <Package size={12} />
                      {p.sku} — {p.description.length > 30 ? p.description.substring(0, 30) + '...' : p.description}
                      <button
                        onClick={() => removeProduct(p.sku)}
                        className="ml-1 text-blue-400 hover:text-blue-600"
                        title="Remover"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">Nenhum filtro de produto aplicado — exibindo todos os pedidos</p>
              )}
            </div>

            {/* Seller + IsPayed + IsFirstPurchase — same row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Seller filter */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Vendedor</label>
                <select
                  className="input text-sm w-full"
                  value={selectedSellerId ?? ''}
                  onChange={e => {
                    setSelectedSellerId(e.target.value ? Number(e.target.value) : undefined);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Todos os vendedores</option>
                  {sellers.map(s => (
                    <option key={s.seller_id} value={s.seller_id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* IsPayed filter */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Status de Pagamento</label>
                <select
                  className="input text-sm w-full"
                  value={selectedIsPayed === undefined ? '' : selectedIsPayed ? 'true' : 'false'}
                  onChange={e => {
                    const val = e.target.value;
                    setSelectedIsPayed(val === '' ? undefined : val === 'true');
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  <option value="true">Pago</option>
                  <option value="false">Não Pago</option>
                </select>
              </div>

              {/* IsFirstPurchase filter */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Primeira Compra</label>
                <select
                  className="input text-sm w-full"
                  value={selectedIsFirstPurchase === undefined ? '' : selectedIsFirstPurchase ? 'true' : 'false'}
                  onChange={e => {
                    const val = e.target.value;
                    setSelectedIsFirstPurchase(val === '' ? undefined : val === 'true');
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  <option value="true">Primeira Compra</option>
                  <option value="false">Compra Recorrente</option>
                </select>
              </div>
            </div>

            {/* Company filter */}
            <CompanyFilterPills selected={selectedCompanyIds} onChange={ids => { setSelectedCompanyIds(ids); setCurrentPage(1); }} />

            {/* Order Type + Sales Channel — same row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Order Type filter */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Tipo de Pedido</label>
                  <button
                    onClick={() => {
                      if (selectedOrderTypeIds.length === orderTypes.length) {
                        setSelectedOrderTypeIds([]);
                      } else {
                        setSelectedOrderTypeIds(orderTypes.map(t => t.order_type_id));
                      }
                      setCurrentPage(1);
                    }}
                    className="text-xs text-emerald-600 hover:underline"
                  >
                    {selectedOrderTypeIds.length === orderTypes.length ? 'Desmarcar todos' : 'Marcar todos'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {orderTypes.map(ot => {
                    const isSelected = selectedOrderTypeIds.includes(ot.order_type_id);
                    return (
                      <button
                        key={ot.order_type_id}
                        onClick={() => {
                          setSelectedOrderTypeIds(prev =>
                            prev.includes(ot.order_type_id)
                              ? prev.filter(id => id !== ot.order_type_id)
                              : [...prev, ot.order_type_id]
                          );
                          setCurrentPage(1);
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                          isSelected
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        {isSelected && <Check size={12} />}
                        {ot.order_type}
                      </button>
                    );
                  })}
                </div>
                {selectedOrderTypeIds.length > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    {selectedOrderTypeIds.length} tipo{selectedOrderTypeIds.length !== 1 ? 's' : ''} selecionado{selectedOrderTypeIds.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              {/* Sales Channel filter */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Canal de Venda</label>
                  <button
                    onClick={() => {
                      if (selectedSalesChannelIds.length === salesChannels.length) {
                        setSelectedSalesChannelIds([]);
                      } else {
                        setSelectedSalesChannelIds(salesChannels.map(c => c.sales_channel_id));
                      }
                      setCurrentPage(1);
                    }}
                    className="text-xs text-emerald-600 hover:underline"
                  >
                    {selectedSalesChannelIds.length === salesChannels.length ? 'Desmarcar todos' : 'Marcar todos'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {salesChannels.map(ch => {
                    const isSelected = selectedSalesChannelIds.includes(ch.sales_channel_id);
                    return (
                      <button
                        key={ch.sales_channel_id}
                        onClick={() => {
                          setSelectedSalesChannelIds(prev =>
                            prev.includes(ch.sales_channel_id)
                              ? prev.filter(id => id !== ch.sales_channel_id)
                              : [...prev, ch.sales_channel_id]
                          );
                          setCurrentPage(1);
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                          isSelected
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        {isSelected && <Check size={12} />}
                        {ch.sales_channel_name}
                      </button>
                    );
                  })}
                </div>
                {selectedSalesChannelIds.length > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    {selectedSalesChannelIds.length} canal{selectedSalesChannelIds.length !== 1 ? 'is' : ''} selecionado{selectedSalesChannelIds.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Payment Method filter */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Meio de Pagamento</label>
                <button
                  onClick={() => {
                    if (selectedPaymentMethodIds.length === paymentMethods.length) {
                      setSelectedPaymentMethodIds([]);
                    } else {
                      setSelectedPaymentMethodIds(paymentMethods.map(pm => pm.payment_method_id));
                    }
                    setCurrentPage(1);
                  }}
                  className="text-xs text-emerald-600 hover:underline"
                >
                  {selectedPaymentMethodIds.length === paymentMethods.length ? 'Desmarcar todos' : 'Marcar todos'}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {paymentMethods.map(pm => {
                  const isSelected = selectedPaymentMethodIds.includes(pm.payment_method_id);
                  return (
                    <button
                      key={pm.payment_method_id}
                      onClick={() => {
                        setSelectedPaymentMethodIds(prev =>
                          prev.includes(pm.payment_method_id)
                            ? prev.filter(id => id !== pm.payment_method_id)
                            : [...prev, pm.payment_method_id]
                        );
                        setCurrentPage(1);
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                        isSelected
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      {isSelected && <Check size={12} />}
                      {pm.payment_method_name}
                    </button>
                  );
                })}
              </div>
              {selectedPaymentMethodIds.length > 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  {selectedPaymentMethodIds.length} meio{selectedPaymentMethodIds.length !== 1 ? 's' : ''} selecionado{selectedPaymentMethodIds.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && !loading && rows.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total Pedidos</p>
              <p className="text-lg font-bold text-slate-900 mt-1">{summary.total_orders}</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Valor Produtos</p>
              <p className="text-lg font-bold text-slate-900 mt-1">{formatCurrency(summary.total_products)}</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total Desconto</p>
              <p className="text-lg font-bold text-red-600 mt-1">{formatCurrency(summary.total_discount)}</p>
            </div>
            <div className="bg-white rounded-lg border border-purple-200 p-3 bg-purple-50">
              <p className="text-xs text-purple-600 uppercase tracking-wider font-medium">Líquido Produtos</p>
              <p className="text-lg font-bold text-purple-700 mt-1">{formatCurrency(summary.total_sales_target ?? ((summary.total_products ?? 0) - (summary.total_discount ?? 0)))}</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total Frete</p>
              <p className="text-lg font-bold text-blue-700 mt-1">{formatCurrency(summary.total_shipping)}</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Outras Despesas</p>
              <p className="text-lg font-bold text-orange-600 mt-1">{formatCurrency(summary.total_other_expenses)}</p>
            </div>
            <div className="bg-white rounded-lg border border-emerald-200 p-3 bg-emerald-50">
              <p className="text-xs text-emerald-600 uppercase tracking-wider font-medium">Total Vendas</p>
              <p className="text-lg font-bold text-emerald-700 mt-1">{formatCurrency(summary.total_sell)}</p>
            </div>
            <div className="bg-white rounded-lg border border-indigo-200 p-3 bg-indigo-50">
              <p className="text-xs text-indigo-600 uppercase tracking-wider font-medium">Ticket Médio</p>
              <p className="text-lg font-bold text-indigo-700 mt-1">
                {formatCurrency(summary.average_ticket ?? (summary.total_orders > 0 ? (summary.total_sell ?? 0) / summary.total_orders : 0))}
              </p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            {error}
            <button className="ml-2 underline" onClick={() => setError(null)}>Fechar</button>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-emerald-600" size={32} />
              </div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <BarChart3 size={48} className="mb-3" />
                <p className="text-lg font-medium">Nenhum resultado encontrado</p>
                <p className="text-sm mt-1">Ajuste os filtros ou período de busca.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1300px] text-sm border-collapse">
                  <TableHeader>
                    <tr>
                      <th className="py-3 px-2 w-8"></th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Pedido</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Meio Pgto.</th>
                      <th className="py-3 px-4 text-center text-xs font-medium text-violet-500 uppercase">1ª Compra</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cidade</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">UF</th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor Produtos</th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Desconto</th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-purple-500 uppercase">Líquido Produtos</th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Frete</th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Outras Desp.</th>
                      <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Total VENDA</th>
                    </tr>
                  </TableHeader>
                  <tbody>
                    {rows.map(row => (
                      <React.Fragment key={row.order_id}>
                        <TableRow>
                          <TableCell className="px-2 w-8">
                            <button
                              onClick={() => toggleExpand(row.order_id)}
                              className="p-1 rounded hover:bg-slate-200 transition-colors text-slate-400 hover:text-slate-600"
                              title={expandedOrderId === row.order_id ? 'Recolher itens' : 'Expandir itens'}
                            >
                              {expandedOrderId === row.order_id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">
                            {row.order_id}
                          </TableCell>
                          <TableCell>{formatDate(row.order_date)}</TableCell>
                          <TableCell>
                            <span className="text-slate-900">{row.customer_name || '—'}</span>
                          </TableCell>
                          <TableCell>{getStatusBadge(row.order_status, row.order_status_id)}</TableCell>
                          <TableCell className="text-slate-600 text-xs">{row.payment_method_name || '—'}</TableCell>
                          <TableCell className="text-center">
                            {row.is_first_purchase ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">1ª</span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-600">{row.ship_addr_city || '—'}</TableCell>
                          <TableCell className="text-slate-600">{row.ship_addr_state || '—'}</TableCell>
                          <TableCell className="text-right font-medium text-slate-800">
                            {formatCurrency(row.order_products_total)}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            {(() => { const d = (row.order_discount ?? 0) + (row.product_replacement_discount_value ?? 0); return d ? `-${formatCurrency(d)}` : formatCurrency(0); })()}
                          </TableCell>
                          <TableCell className="text-right font-medium text-purple-700">
                            {formatCurrency(row.order_sales_target ?? ((row.order_products_total ?? 0) - ((row.order_discount ?? 0) + (row.product_replacement_discount_value ?? 0))))}
                          </TableCell>
                          <TableCell className="text-right text-slate-600">
                            {formatCurrency(row.shipping_price)}
                          </TableCell>
                          <TableCell className="text-right text-orange-600">
                            {formatCurrency(row.other_expenses)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-emerald-700">
                            {formatCurrency(row.order_sell_total)}
                          </TableCell>
                        </TableRow>

                        {/* Expanded items */}
                        {expandedOrderId === row.order_id && (
                          <tr>
                            <td colSpan={15} className="p-0 border-b border-slate-200">
                              {orderItemsCache[row.order_id]?.loading ? (
                                <div className="flex items-center gap-2 px-6 py-4 text-sm text-slate-500 bg-slate-50">
                                  <Loader2 size={16} className="animate-spin text-emerald-600" />
                                  Carregando itens...
                                </div>
                              ) : (
                                <div className="overflow-x-auto bg-emerald-50/20">
                                  <table className="w-full text-sm border-collapse">
                                    <thead>
                                      <tr className="bg-slate-100 border-b border-slate-200">
                                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">SKU</th>
                                        <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Descrição</th>
                                        <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Qtd</th>
                                        <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Devolvido</th>
                                        <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Valor Unit.</th>
                                        <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Desconto (%)</th>
                                        <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(orderItemsCache[row.order_id]?.items ?? []).length > 0 ? (
                                        (orderItemsCache[row.order_id]?.items ?? []).map(item => (
                                          <tr key={item.order_item_id} className={`border-b border-slate-100 transition-colors ${item.is_fully_returned ? 'line-through opacity-60 text-gray-400 bg-slate-50' : 'bg-white hover:bg-emerald-50/60'}`}>
                                            <td className="px-4 py-2 font-mono font-semibold text-slate-800 text-xs">{item.sku}</td>
                                            <td className="px-4 py-2 text-slate-700 text-xs">
                                              <span className="flex items-center gap-2">
                                                {item.description || '—'}
                                                {item.is_fully_returned && (
                                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-600 no-underline" style={{ textDecoration: 'none' }}>Devolvido</span>
                                                )}
                                                {!item.is_fully_returned && (item.returned_quantity ?? 0) > 0 && (
                                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 no-underline" style={{ textDecoration: 'none' }}>Devolução parcial</span>
                                                )}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2 text-center text-xs text-slate-700">
                                              {item.effective_quantity ?? item.quantity}
                                              {!item.is_fully_returned && (item.returned_quantity ?? 0) > 0 && (
                                                <span className="block text-[10px] text-slate-400">(orig: {item.quantity})</span>
                                              )}
                                            </td>
                                            <td className="px-4 py-2 text-center text-xs">
                                              {(item.returned_quantity ?? 0) > 0
                                                ? <span className="text-red-600 font-medium">{item.returned_quantity}</span>
                                                : <span className="text-slate-300">—</span>
                                              }
                                            </td>
                                            <td className="px-4 py-2 text-right text-xs text-slate-700">{formatCurrency(item.unit_price)}</td>
                                            <td className="px-4 py-2 text-right text-xs text-amber-600">{item.discount ? `${item.discount}%` : '—'}</td>
                                            <td className="px-4 py-2 text-right text-xs font-semibold text-emerald-700">
                                              {formatCurrency(item.effective_total ?? ((item.unit_price || 0) * (1 - (item.discount || 0) / 100) * (item.effective_quantity ?? item.quantity)))}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan={7} className="px-4 py-4 text-center text-slate-400 text-sm italic">Nenhum item neste pedido</td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  {/* Summary footer */}
                  {summary && (
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                      <tr>
                        <td colSpan={9} className="py-3 px-4 text-right font-bold text-slate-700 uppercase text-sm">
                          Totais ({summary.total_orders} pedidos)
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-slate-900">
                          {formatCurrency(summary.total_products)}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-red-600">
                          -{formatCurrency(summary.total_discount)}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-purple-700">
                          {formatCurrency(summary.total_sales_target ?? ((summary.total_products ?? 0) - (summary.total_discount ?? 0)))}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-slate-900">
                          {formatCurrency(summary.total_shipping)}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-orange-600">
                          {formatCurrency(summary.total_other_expenses)}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-emerald-700 text-lg">
                          {formatCurrency(summary.total_sell)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-1">
          <p className="text-sm text-slate-505 order-2 sm:order-1">
            {totalItems > 0
              ? `${((currentPage - 1) * pageSize) + 1}–${Math.min(currentPage * pageSize, totalItems)} de ${totalItems} registro${totalItems !== 1 ? 's' : ''} · Página ${currentPage} de ${totalPages}`
              : 'Nenhum registro'}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1 order-1 sm:order-2">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(1)}
                title="Primeira página"
                className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-303 bg-white text-slate-707 hover:bg-slate-55 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >«</button>
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => p - 1)}
                title="Página anterior"
                className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-303 bg-white text-slate-707 hover:bg-slate-55 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >‹</button>
              {getPageRange(currentPage, totalPages).map((p, i) =>
                p === '...' ? (
                  <span key={`ell-${i}`} className="w-9 h-9 flex items-center justify-center text-slate-404 text-sm">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(Number(p))}
                    title={`Página ${p}`}
                    className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${
                      currentPage === p
                        ? 'bg-emerald-606 text-white border-emerald-606'
                        : 'bg-white text-slate-707 border-slate-303 hover:bg-slate-55'
                    }`}
                  >{p}</button>
                )
              )}
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                title="Próxima página"
                className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-303 bg-white text-slate-707 hover:bg-slate-55 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >›</button>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(totalPages)}
                title="Última página"
                className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-303 bg-white text-slate-707 hover:bg-slate-55 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >»</button>
            </div>
          )}
        </div>

        {/* Product Search Modal */}
        <ProductSearchModal
          isOpen={showProductModal}
          onClose={() => setShowProductModal(false)}
          onSelect={addProduct}
          excludeSkus={selectedProducts.map(p => p.sku)}
        />
        </>
        )}
      </div>
    </PermissionGuard>
  );
};
